/**
 * Model Top Picks dashboard — ranks every PL player by `tutovka_score`
 * (mean × consistency × prior boost × FDR). Powered by the cached backend
 * endpoint /api/lineup-sim/top-picks.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
    ArrowLeft, ArrowsClockwise, Crown, Lightning, Sparkle,
    Trophy, Funnel,
} from "@phosphor-icons/react";
import AppShell from "../components/AppShell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const POS_COLOR = {
    GKP: { text: "text-fa-yellow", bg: "bg-fa-yellow/10" },
    DEF: { text: "text-fa-mint", bg: "bg-fa-mint/10" },
    MID: { text: "text-blue-300", bg: "bg-blue-300/10" },
    FWD: { text: "text-rose-300", bg: "bg-rose-300/10" },
};

const AGE_LABEL = {
    young: { tag: "YOUNG", color: "text-fa-mint" },
    developing: { tag: "DEV", color: "text-fa-mint" },
    peak: { tag: "PEAK", color: "text-white/60" },
    veteran: { tag: "VET", color: "text-orange-300" },
    aging: { tag: "AGE", color: "text-red-300" },
};

const PHASES = [
    ["", "Vše"],
    ["gw1_6", "Start 1–6"],
    ["gw7_12", "Třetina 7–12"],
    ["gw13_18", "Př. Vánoci 13–18"],
    ["gw19_24", "Vánoce 19–24"],
    ["gw25_30", "Př. finišem 25–30"],
    ["gw31_38", "Run-in 31–38"],
];

function fdrColor(fdr) {
    if (fdr == null) return "text-white/40";
    if (fdr < 2.3) return "text-green-300";
    if (fdr < 3.0) return "text-fa-mint";
    if (fdr < 3.6) return "text-fa-yellow";
    if (fdr < 4.3) return "text-orange-300";
    return "text-red-300";
}

export default function LineupSimTopPicks() {
    const [mode, setMode] = useState("fantasy");
    const [position, setPosition] = useState("ALL");
    const [maxPrice, setMaxPrice] = useState("");
    const [sortBy, setSortBy] = useState("tutovka_score");
    const [phase, setPhase] = useState("");
    const [colSort, setColSort] = useState(null); // {key, dir} — client-side column sort
    const [busy, setBusy] = useState(false);
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);
    const [computing, setComputing] = useState(false);
    const pollRef = useRef(null);

    const load = async (force = false, isPoll = false) => {
        if (!isPoll) setBusy(true);
        setErr(null);
        try {
            const params = new URLSearchParams({
                mode,
                sort_by: sortBy,
                limit: "60",
            });
            if (position !== "ALL") params.set("position", position);
            if (maxPrice && parseFloat(maxPrice) > 0) params.set("max_price", maxPrice);
            if (phase) params.set("phase", phase);
            if (force) params.set("force_refresh", "true");
            const r = await axios.get(`${API}/lineup-sim/top-picks?${params}`);
            if (r.status === 202 || r.data?.status === "computing") {
                setComputing(true);
                clearTimeout(pollRef.current);
                pollRef.current = setTimeout(() => load(false, true), 5000);
                return;
            }
            setComputing(false);
            setData(r.data);
            if (r.data?.refreshing) {
                clearTimeout(pollRef.current);
                pollRef.current = setTimeout(() => load(false, true), 15000);
            }
        } catch (e) {
            if (e?.response?.status === 202) {
                setComputing(true);
                clearTimeout(pollRef.current);
                pollRef.current = setTimeout(() => load(false, true), 5000);
                return;
            }
            setErr(e?.response?.data?.detail || "Load failed");
        } finally {
            if (!isPoll) setBusy(false);
        }
    };

    useEffect(() => {
        load(false);
        return () => clearTimeout(pollRef.current);
        /* eslint-disable-next-line */
    }, [mode, position, maxPrice, sortBy, phase]);

    const rows = data?.rows || [];

    // Client-side column sort over the loaded rows.
    const displayRows = useMemo(() => {
        if (!colSort) return rows;
        return [...rows].sort((a, b) => {
            const va = a[colSort.key] ?? -Infinity;
            const vb = b[colSort.key] ?? -Infinity;
            return colSort.dir === "asc" ? va - vb : vb - va;
        });
    }, [rows, colSort]);

    const clickCol = (key) =>
        setColSort((cur) => cur?.key === key
            ? (cur.dir === "desc" ? { key, dir: "asc" } : null)
            : { key, dir: "desc" });

    const SortTh = ({ colKey, label, align = "right" }) => (
        <th className={`px-1 py-2 ${align === "right" ? "text-right" : ""}`}>
            <button
                type="button"
                onClick={() => clickCol(colKey)}
                data-testid={`col-sort-${colKey}`}
                title={`Seřadit podle ${label}`}
                className={`uppercase tracking-widest hover:text-fa-yellow ${colSort?.key === colKey ? "text-fa-yellow font-bold" : ""}`}
            >
                {label}{colSort?.key === colKey ? (colSort.dir === "desc" ? " ▼" : " ▲") : ""}
            </button>
        </th>
    );


    // Group top-N per position (for the "TOP TUTOVKY" summary cards at the top)
    const byPos = useMemo(() => {
        const out = { GKP: [], DEF: [], MID: [], FWD: [] };
        const sorted = [...(data?.rows || [])].sort((a, b) => b.tutovka_score - a.tutovka_score);
        for (const r of sorted) {
            if (!out[r.position]) continue;
            if (out[r.position].length < 3) out[r.position].push(r);
        }
        return out;
    }, [data]);

    return (
        <AppShell>
            <section className="space-y-5" data-testid="top-picks-page">
                <header className="space-y-2">
                    <Link to="/lineup-sim" className="font-mono text-[10px] uppercase tracking-widest text-white/50 hover:text-white inline-flex items-center gap-1.5">
                        <ArrowLeft size={11} weight="bold" /> Lineup Sim
                    </Link>
                    <div className="flex items-end justify-between gap-3 flex-wrap">
                        <div>
                            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-fa-yellow flex items-center gap-2 mb-1">
                                <Sparkle size={11} weight="fill" />
                                Model · Top picks dashboard
                            </div>
                            <h1 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tight">
                                Tutovky <span className="text-fa-yellow">podle modelu</span>
                            </h1>
                            <p className="font-mono text-[11px] text-white/65 mt-2 max-w-2xl leading-relaxed">
                                Ranking všech ~200 PL hráčů podle skóre kombinujícího
                                <strong> mean × consistency × prior boost × FDR</strong>. Mladí dostávají +20%,
                                veteráni -3.5 až -5%, injury-year tolerance odpouští 1 špatnou sezónu.
                            </p>
                        </div>
                        <div className="font-mono text-[9px] uppercase tracking-widest text-white/40 text-right">
                            <div>{data?.from_cache ? <>Cached · {data?.age_hours?.toFixed(1)}h old</> : <>Live compute</>}</div>
                            <div>{data?.computed_at && new Date(data.computed_at).toLocaleString()}</div>
                            <button type="button" onClick={() => load(true)}
                                    disabled={busy}
                                    data-testid="refresh-btn"
                                    className="mt-1 font-mono text-[10px] uppercase tracking-widest text-fa-yellow border border-fa-yellow/40 px-2 py-1 hover:bg-fa-yellow hover:text-black disabled:opacity-30">
                                <ArrowsClockwise size={9} weight="bold" className={`inline mb-0.5 mr-1 ${busy ? "animate-spin" : ""}`} />
                                Force refresh
                            </button>
                        </div>
                    </div>
                </header>

                {/* Filter bar */}
                <div className="border border-white/10 bg-white/[0.02] p-3 flex flex-wrap items-center gap-3"
                     data-testid="filter-bar">
                    <span className="font-mono text-[10px] uppercase tracking-widest text-white/45 flex items-center gap-1.5">
                        <Funnel size={11} weight="fill" /> Filters
                    </span>
                    <div className="flex gap-1">
                        {["fantasy", "standard"].map((m) => (
                            <button key={m} type="button" onClick={() => setMode(m)}
                                    data-testid={`mode-${m}`}
                                    className={`font-mono text-[10px] uppercase tracking-widest px-2 py-1 ${mode === m ? "bg-fa-yellow text-black font-bold" : "border border-white/10 text-white/60 hover:text-white"}`}>
                                {m}
                            </button>
                        ))}
                    </div>
                    <div className="flex gap-1">
                        {["ALL", "GKP", "DEF", "MID", "FWD"].map((p) => (
                            <button key={p} type="button" onClick={() => setPosition(p)}
                                    data-testid={`pos-${p}`}
                                    className={`font-mono text-[10px] uppercase tracking-widest px-2 py-1 ${position === p ? "bg-fa-yellow text-black font-bold" : "border border-white/10 text-white/60 hover:text-white"}`}>
                                {p}
                            </button>
                        ))}
                    </div>
                    <label className="font-mono text-[10px] uppercase tracking-widest text-white/55 flex items-center gap-1.5">
                        Max £
                        <input type="number" value={maxPrice} step={0.1} min={3.5} max={20} placeholder="any"
                               onChange={(e) => setMaxPrice(e.target.value)}
                               data-testid="max-price-input"
                               className="bg-black/40 border border-white/15 px-1.5 py-0.5 w-20 text-white text-[11px] tabular-nums focus:outline-none focus:border-fa-yellow"/>
                    </label>
                    <div className="flex gap-1">
                        {[["tutovka_score", "Score"], ["value", "Value"]].map(([k, l]) => (
                            <button key={k} type="button" onClick={() => setSortBy(k)}
                                    data-testid={`sort-${k}`}
                                    className={`font-mono text-[10px] uppercase tracking-widest px-2 py-1 ${sortBy === k ? "bg-fa-yellow text-black font-bold" : "border border-white/10 text-white/60 hover:text-white"}`}>
                                Sort: {l}
                            </button>
                        ))}
                    </div>
                    <div className="flex gap-1 flex-wrap items-center">
                        <span className="font-mono text-[10px] uppercase tracking-widest text-white/45">Fáze</span>
                        {PHASES.map(([k, l]) => (
                            <button key={k || "all"} type="button" onClick={() => setPhase(k)}
                                    data-testid={`phase-${k || "all"}`}
                                    title={k ? "FDR se počítá jen ze zápasů této fáze sezóny 2026-27" : "FDR z nejbližších GW"}
                                    className={`font-mono text-[10px] uppercase tracking-widest px-2 py-1 ${phase === k ? "bg-fa-mint text-black font-bold" : "border border-white/10 text-white/60 hover:text-white"}`}>
                                {l}
                            </button>
                        ))}
                    </div>
                </div>

                {phase && (
                    <div className="font-mono text-[10px] text-fa-mint/90 border border-fa-mint/25 bg-fa-mint/[0.04] p-2">
                        Skóre přepočteno pro fázi <strong>{PHASES.find(([k]) => k === phase)?.[1]}</strong> —
                        FDR složka vychází z průměrné obtížnosti losu týmu v těchto GW (rozpis 2026-27).
                    </div>
                )}

                {err && <div className="border border-red-300/40 bg-red-300/10 p-3 font-mono text-[11px] text-red-200">{err}</div>}
                {computing && (
                    <div className="border border-fa-yellow/40 bg-fa-yellow/5 p-3 font-mono text-[11px] text-fa-yellow flex items-center gap-2" data-testid="top-picks-computing">
                        <ArrowsClockwise size={13} className="animate-spin" />
                        Model se právě počítá na pozadí (~30 s) — stránka se obnoví automaticky.
                    </div>
                )}

                {busy && !data && (
                    <div className="border border-white/10 bg-white/[0.02] p-6 text-center font-mono text-[11px] text-white/60">
                        <ArrowsClockwise size={20} weight="bold" className="animate-spin inline mb-2" />
                        <div>Computing scores for ~200 players… (first run ~30s, then cached 6h)</div>
                    </div>
                )}

                {/* TOP-3 per position summary cards (only when no position filter) */}
                {data && position === "ALL" && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="pos-summary">
                        {["GKP", "DEF", "MID", "FWD"].map((pos) => (
                            <div key={pos} className="border border-white/10 bg-[#0d0d0d] p-3">
                                <div className={`font-mono text-[9px] uppercase tracking-[0.3em] mb-2 ${POS_COLOR[pos]?.text || "text-white"}`}>
                                    <Trophy size={9} weight="fill" className="inline mb-0.5 mr-1" />
                                    Top {pos}
                                </div>
                                <ol className="space-y-1.5">
                                    {(byPos[pos] || []).map((r, i) => (
                                        <li key={r.id} className="font-mono text-[11px] leading-tight flex items-center justify-between gap-2"
                                            data-testid={`top-${pos}-${i}`}>
                                            <span className="truncate">
                                                <span className="text-fa-yellow font-bold">{i + 1}.</span> <span className="text-white">{r.name}</span>
                                                <span className="text-white/30 text-[9px] ml-1">{r.team_short}·£{r.price}</span>
                                                {r.prior_boost > 1.01 && <span className="text-fa-mint text-[8px] ml-1">↑{Math.round((r.prior_boost - 1) * 100)}%</span>}
                                                {r.prior_boost < 0.99 && <span className="text-orange-300 text-[8px] ml-1">↓{Math.round((1 - r.prior_boost) * 100)}%</span>}
                                                {r.injury_year_last && <span className="text-orange-200 text-[8px] ml-1 bg-orange-500/15 px-1">INJ</span>}
                                            </span>
                                            <span className="font-bold text-fa-yellow tabular-nums">{r.tutovka_score?.toFixed(1)}</span>
                                        </li>
                                    ))}
                                </ol>
                            </div>
                        ))}
                    </div>
                )}

                {/* Full ranked table */}
                {data && rows.length > 0 && (
                    <div className="border border-white/10 bg-[#0d0d0d] overflow-x-auto" data-testid="full-table">
                        <table className="w-full text-left">
                            <thead className="bg-white/[0.02] border-b border-white/10 sticky top-0">
                                <tr className="font-mono text-[9px] uppercase tracking-widest text-white/40">
                                    <th className="px-2 py-2">#</th>
                                    <th className="px-2 py-2">Player</th>
                                    <th className="px-1 py-2">Pos</th>
                                    <th className="px-1 py-2">Team</th>
                                    <SortTh colKey="price" label="£" />
                                    <SortTh colKey="mean" label="Avg" />
                                    <SortTh colKey="ceiling" label="Ceil" />
                                    <SortTh colKey="blank_rate" label="Blank%" />
                                    <SortTh colKey="haul_rate" label="Haul%" />
                                    <SortTh colKey="prior_boost" label="Boost" />
                                    <SortTh colKey="fdr_avg" label="FDR" />
                                    <SortTh colKey="tutovka_score" label="Score" />
                                    <SortTh colKey="value_score" label="Value" />
                                    <SortTh colKey="selected_by_percent" label="Sel%" align="left" />
                                </tr>
                            </thead>
                            <tbody>
                                {displayRows.map((r, i) => {
                                    const age = AGE_LABEL[r.age_proxy] || { tag: "?", color: "text-white/40" };
                                    return (
                                        <tr key={r.id} className="border-b border-white/5 hover:bg-fa-yellow/[0.03]"
                                            data-testid={`row-${r.id}`}>
                                            <td className="px-2 py-1.5 font-mono text-[10px] text-fa-yellow tabular-nums">{i + 1}</td>
                                            <td className="px-2 py-1.5 font-display font-black text-sm">
                                                {r.name}
                                                {r.prior_boost > 1.01 && (
                                                    <span className="ml-1 font-mono text-[8px] font-bold text-fa-mint"
                                                          title={(r.prior_notes || []).join(" · ")}>
                                                        ↑{Math.round((r.prior_boost - 1) * 100)}%
                                                    </span>
                                                )}
                                                {r.prior_boost < 0.99 && (
                                                    <span className="ml-1 font-mono text-[8px] font-bold text-orange-300"
                                                          title={(r.prior_notes || []).join(" · ")}>
                                                        ↓{Math.round((1 - r.prior_boost) * 100)}%
                                                    </span>
                                                )}
                                                {r.injury_year_last && (
                                                    <span className="ml-1 font-mono text-[8px] text-orange-200 bg-orange-500/15 px-1"
                                                          title="Last season looked injury-affected — excluded from baseline">
                                                        INJ
                                                    </span>
                                                )}
                                                {r.news && (
                                                    <span className="ml-1 font-mono text-[8px] text-red-200 bg-red-500/15 px-1"
                                                          title={r.news}>!</span>
                                                )}
                                                <span className={`ml-1 font-mono text-[8px] ${age.color}`}>{age.tag}</span>
                                            </td>
                                            <td className={`px-1 py-1.5 font-mono text-[10px] uppercase tracking-widest font-bold ${POS_COLOR[r.position]?.text || "text-white"}`}>{r.position}</td>
                                            <td className="px-1 py-1.5 font-mono text-[10px] text-white/60">{r.team_short}</td>
                                            <td className="px-1 py-1.5 font-mono text-[10px] tabular-nums text-white/70 text-right">£{r.price}</td>
                                            <td className="px-1 py-1.5 font-mono text-[10px] tabular-nums text-fa-yellow font-bold text-right">{r.mean}</td>
                                            <td className="px-1 py-1.5 font-mono text-[10px] tabular-nums text-white/50 text-right">{r.ceiling?.toFixed(1)}</td>
                                            <td className="px-1 py-1.5 font-mono text-[10px] tabular-nums text-white/60 text-right">{Math.round(r.blank_rate * 100)}%</td>
                                            <td className="px-1 py-1.5 font-mono text-[10px] tabular-nums text-fa-mint text-right">{Math.round(r.haul_rate * 100)}%</td>
                                            <td className="px-1 py-1.5 font-mono text-[10px] tabular-nums text-white/70 text-right">{r.prior_boost?.toFixed(2)}</td>
                                            <td className={`px-1 py-1.5 font-mono text-[10px] tabular-nums font-bold text-right ${fdrColor(r.fdr_avg)}`}>{r.fdr_avg}</td>
                                            <td className="px-1 py-1.5 font-mono text-[10px] tabular-nums text-fa-yellow font-black text-right">{r.tutovka_score}</td>
                                            <td className="px-1 py-1.5 font-mono text-[10px] tabular-nums text-fa-mint text-right">{r.value_score}</td>
                                            <td className="px-1 py-1.5 font-mono text-[10px] tabular-nums text-white/40">{r.selected_by_percent?.toFixed(0)}%</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}

                <div className="font-mono text-[10px] text-white/45 leading-relaxed border-l-2 border-fa-yellow/40 pl-3">
                    <Lightning size={10} weight="fill" className="inline text-fa-yellow mb-0.5 mr-1" />
                    <strong>Skóre formula:</strong> <code>mean × (1 - blank/2) × (1 + haul) × boost × fdr_mult</code>.
                    Cache se obnovuje každých 6h. Klikni "Force refresh" pro okamžitý přepočet.
                </div>
            </section>
        </AppShell>
    );
}
